import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useStripe } from '@stripe/react-stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Button, RadioOrCheckbox, Typography } from '@3divi/baseui';
import tr from 'locale';
import { BASE_URL } from 'Config';
import Header from 'Header';
import HeaderContainer from 'components/HeaderContainer';
import Footer from 'components/elements/Footer/';
import PayPalButton from 'components/elements/PaypalButton';
import { usePaypalOptions } from 'components/elements/PaypalButton/usePaypalOptions';
import formatDate from 'FormatDate';
import {
  switchBillingProvider,
  bulkSwitchBillingProvider,
  completeBillingProviderSwitch,
} from 'api';
import { fetchLicensesAction, fetchGroupsAction } from 'store/licenses/actions';
import { fetchProfile } from 'store/clientdata/actions';
import { getDataSelector } from 'hooks';

const Box = styled.main`
  flex: 1 1 0;
  display: flex;
  flex-flow: column nowrap;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 8px 32px 0;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);

  @media (max-width: 720px) {
    padding: 8px 16px 0;
  }
`;

const Content = styled.div`
  flex: 1 1 320px;
  max-width: 720px;
`;

const List = styled.div`
  margin: 16px 0;
  border: 1px solid ${({ theme }) => theme.border.default};
  border-radius: 4px;
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 4px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.border.default};
  background: ${({ theme }) => theme.background.primary};

  &:last-child {
    border-bottom: none;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin: 16px 0 24px;
`;

const Stepper = styled.div`
  margin: 24px 0;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.border.default};
  border-radius: 4px;
  background: ${({ theme }) => theme.background.primary};
`;

const Hint = styled.div`
  margin: 8px 0 16px;
`;

const ErrorText = styled.div`
  margin: 8px 0;
  color: #c62828;
`;

function getCommonTargets(licenses) {
  if (!licenses.length) return [];
  return licenses.reduce((acc, license) => {
    const targets = license.switch_provider_targets || [];
    if (!acc) return [...targets];
    return acc.filter(target => targets.includes(target));
  }, null);
}

function getTargetLabel(targets) {
  if ((targets || []).includes('paypal')) return 'PayPal';
  if ((targets || []).includes('stripe')) return 'Stripe';
  return '';
}

function BillingSwitchPage() {
  const { platform } = useParams();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const subscriptionPaypalOptions = usePaypalOptions(true);

  const licenses = useSelector(state => getDataSelector(state.licenses, 'licenses', platform));
  const licensesLoadComplete = useSelector(state => state.licenses.fetchLicenses.success);

  const switchableLicenses = useMemo(() => {
    return licenses
      .filter(license => license.can_switch_provider)
      .sort((a, b) => a.id - b.id);
  }, [licenses]);

  const queryLicenseId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('license_id');
    return raw ? Number(raw) : null;
  }, [location.search]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [phase, setPhase] = useState('select');
  const [stepIndex, setStepIndex] = useState(0);
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preselected, setPreselected] = useState(false);
  // Cache PayPal subscription id per license; PayPal Buttons may call create more than once.
  const [paypalSubByLicense, setPaypalSubByLicense] = useState({});
  const paypalSubByLicenseRef = useRef(paypalSubByLicense);
  paypalSubByLicenseRef.current = paypalSubByLicense;

  useEffect(() => {
    dispatch(fetchGroupsAction());
    dispatch(fetchLicensesAction());
  }, [dispatch, platform]);

  useEffect(() => {
    if (preselected || !licensesLoadComplete || !queryLicenseId) return;
    if (switchableLicenses.some(l => l.id === queryLicenseId)) {
      setSelectedIds([queryLicenseId]);
      setPreselected(true);
    }
  }, [licensesLoadComplete, queryLicenseId, switchableLicenses, preselected]);

  const selectedLicenses = useMemo(
    () => switchableLicenses.filter(license => selectedIds.includes(license.id)),
    [switchableLicenses, selectedIds]
  );
  const commonTargets = getCommonTargets(selectedLicenses);
  const targetProvider = commonTargets.length === 1 ? commonTargets[0] : null;

  const toggleLicense = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === switchableLicenses.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(switchableLicenses.map(license => license.id));
    }
  };

  const goDashboard = () => {
    history.push(`${BASE_URL}/${platform}/dashboard/`);
  };

  const startSwitch = async () => {
    setError(null);
    if (!targetProvider || !selectedLicenses.length) return;

    if (targetProvider === 'paypal') {
      setQueue(selectedLicenses);
      setStepIndex(0);
      setPaypalSubByLicense({});
      setPhase('paypal_stepper');
      return;
    }

    if (targetProvider === 'stripe') {
      setBusy(true);
      setPhase('stripe_redirect');
      try {
        const response = await bulkSwitchBillingProvider({
          licenseIds: selectedLicenses.map(license => license.id),
          provider: 'stripe',
          platform,
        });
        if (!stripe) {
          throw new Error('Stripe is not ready');
        }
        await stripe.redirectToCheckout({
          sessionId: response.payment.provider_data.session_id,
        });
      } catch (err) {
        console.error(err);
        setError(tr('billing_switch_error'));
        setPhase('select');
        setBusy(false);
      }
    }
  };

  const createPaypalSubscription = useCallback(() => {
    const license = queue[stepIndex];
    const cached = paypalSubByLicenseRef.current[license.id];
    if (cached) {
      return Promise.resolve(cached);
    }

    setError(null);
    return switchBillingProvider({
      licenseId: license.id,
      provider: 'paypal',
      platform,
    })
      .then(response => {
        const subscriptionId = response && response.subscription_id;
        if (!subscriptionId) {
          throw new Error('Missing PayPal subscription_id in switch response');
        }
        setPaypalSubByLicense(prev => ({ ...prev, [license.id]: subscriptionId }));
        return subscriptionId;
      })
      .catch(err => {
        console.error(err);
        setError(tr('billing_switch_fulfill_error'));
        throw err;
      });
  }, [queue, stepIndex, platform]);

  const fulfillPaypalSubscription = useCallback(
    subscriptionId => {
      const license = queue[stepIndex];
      return completeBillingProviderSwitch({
        licenseId: license.id,
        subscriptionId,
      });
    },
    [queue, stepIndex]
  );

  const onPaypalApproved = useCallback(async () => {
    dispatch(fetchProfile());
    await dispatch(fetchLicensesAction());
    const nextIndex = stepIndex + 1;
    if (nextIndex >= queue.length) {
      setPhase('done');
      setTimeout(() => {
        history.push(`${BASE_URL}/${platform}/dashboard/`);
      }, 1200);
      return;
    }
    setStepIndex(nextIndex);
  }, [dispatch, stepIndex, queue.length, history, platform]);

  const onPaypalError = useCallback(
    reason => {
      console.error(reason);
      setError(tr('billing_switch_fulfill_error'));
      dispatch(fetchProfile());
      dispatch(fetchLicensesAction());
    },
    [dispatch]
  );

  const currentStepLicense = queue[stepIndex];

  return (
    <Box>
      <Content>
        <Header title={tr('billing_switch_page_title')} />
        <HeaderContainer pageName={tr('billing_switch_page_title')} />
        <Typography size="15" height="22" color="secondary">
          {tr('billing_switch_page_subtitle')}
        </Typography>

        {phase === 'select' && (
          <>
            {!switchableLicenses.length ? (
              <Hint>
                <Typography size="15" height="22">
                  {tr('billing_switch_empty')}
                </Typography>
                <Actions>
                  <Button
                    text={tr('billing_switch_back')}
                    size="m"
                    theme="outline"
                    onClick={goDashboard}
                  />
                </Actions>
              </Hint>
            ) : (
              <>
                <Actions>
                  <Button
                    text={tr('billing_switch_select_all')}
                    size="s"
                    theme="outline"
                    onClick={toggleAll}
                  />
                  <Typography size="14" height="20" color="secondary">
                    {tr('billing_switch_selected', { count: selectedIds.length })}
                  </Typography>
                </Actions>
                <List>
                  {switchableLicenses.map(license => (
                    <Row key={license.id}>
                      <RadioOrCheckbox
                        type="checkbox"
                        checked={selectedIds.includes(license.id)}
                        onClick={() => toggleLicense(license.id)}
                        title={
                          <div>
                            <Typography size="15" height="20" weight="500">
                              {license.title || `#${license.id}`}
                            </Typography>
                            <Typography size="13" height="18" color="secondary">
                              {license.billing_grace_until
                                ? `${formatDate(license.billing_grace_until)} → ${getTargetLabel(
                                    license.switch_provider_targets
                                  )}`
                                : getTargetLabel(license.switch_provider_targets)}
                            </Typography>
                          </div>
                        }
                      />
                    </Row>
                  ))}
                </List>
                {selectedLicenses.length > 0 && !targetProvider && (
                  <ErrorText>{tr('billing_switch_incompatible')}</ErrorText>
                )}
                {error && <ErrorText>{error}</ErrorText>}
                <Actions>
                  <Button
                    text={
                      targetProvider === 'paypal'
                        ? tr('billing_switch_target_paypal')
                        : targetProvider === 'stripe'
                        ? tr('billing_switch_target_stripe')
                        : tr('billing_switch_start')
                    }
                    size="m"
                    theme="contained"
                    color="primary"
                    disabled={!targetProvider || busy}
                    onClick={startSwitch}
                  />
                  <Button
                    text={tr('billing_switch_back')}
                    size="m"
                    theme="outline"
                    onClick={goDashboard}
                  />
                </Actions>
              </>
            )}
          </>
        )}

        {phase === 'paypal_stepper' && currentStepLicense && (
          <Stepper>
            <Typography size="18" height="24" weight="600">
              {tr('billing_switch_stepper_title', {
                current: stepIndex + 1,
                total: queue.length,
              })}
            </Typography>
            <Hint>
              <Typography size="14" height="20" color="secondary">
                {currentStepLicense.title || `#${currentStepLicense.id}`}
              </Typography>
              <Typography size="14" height="20" color="secondary">
                {tr('billing_switch_stepper_hint')}
              </Typography>
            </Hint>
            {subscriptionPaypalOptions && (
              <PayPalScriptProvider options={subscriptionPaypalOptions}>
                <PayPalButton
                  key={currentStepLicense.id}
                  createSubscription={createPaypalSubscription}
                  fulfillSubscription={fulfillPaypalSubscription}
                  onApprove={onPaypalApproved}
                  onError={onPaypalError}
                />
              </PayPalScriptProvider>
            )}
            {error && <ErrorText>{error}</ErrorText>}
            <Actions>
              <Button
                text={tr('billing_switch_back')}
                size="m"
                theme="outline"
                onClick={() => {
                  setPhase('select');
                  setError(null);
                  setPaypalSubByLicense({});
                }}
              />
            </Actions>
          </Stepper>
        )}

        {phase === 'stripe_redirect' && (
          <Hint>
            <Typography size="15" height="22">
              {tr('billing_switch_target_stripe')}…
            </Typography>
          </Hint>
        )}

        {phase === 'done' && (
          <Hint>
            <Typography size="15" height="22">
              {tr('billing_switch_done')}
            </Typography>
          </Hint>
        )}
      </Content>
      <Footer />
    </Box>
  );
}

export default BillingSwitchPage;
